// /*=========================================================================================
//   File Name: router.js
//   Description: Routes for vue-router. Lazy loading is enabled.
//   Object Structure:
//                     path => router path
//                     name => router name
//                     component(lazy loading) => component to load
//                     meta : {
//                       rule => which user can have access (ACL)
//                       breadcrumb => Add breadcrumb to specific page
//                       pageTitle => Display title besides breadcrumb
//                     }
//   ----------------------------------------------------------------------------------------
//   Item Name: GetBoarded UI Template
//   Author: GetBoarded
//   Author URL: https://www.getboarded.com
// ==========================================================================================*/

/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import auth from "@/auth/authService";
import i18n from "./i18n/i18n";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "/",
          redirect: "/user/home",
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user",
          redirect: "/user/home",
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/admin",
          redirect: "/admin/user",
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: '/insights',
          name: 'page-insights',
          component: () => import('@/views/insights/InsightsView.vue'),
          meta: {
            rule: 'admin', // Or whatever role system you use
            title: 'Insights'
          }
        },
        {
          path: "/admin/home",
          redirect: "/admin/user",
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/admin/users",
          name: "admin-user-list",
          component: () => import("@/views/admin/user/User.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/"},
              { title: i18n.t('User') },
              { title: i18n.t('List'), active: true }
            ],
            pageTitle: i18n.t('userlist'),
            rule: "admin",
            authRequired: true,
          }
        },
        {
          path: "/admin/schedules",
          name: "calender",
          component: () => import("@/views/admin/user/calender/Calender.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/"},
              { title: i18n.t('Schedules') },
            ],
            pageTitle: i18n.t('Schedules'),
            rule: "admin",
            authRequired: true,
          }
        },
        {
          path: "/admin/consultants",
          name: "admin-consultants-list",
          component: () => import("@/views/admin/consultants/ConsultantsList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Consultants", active: true }
            ],
            pageTitle: "Consultants",
            rule: "admin",
            authRequired: true,
          }
        },
        {
          path: "/admin/user/user-view/:usersId",
          name: "admin-user-view",
          component: () => import("@/views/admin/user/UserView.vue"),
          meta: {
            breadcrumb: [
              { title: i18n.t('User') },
              { title: "View", active: true }
            ],
            pageTitle: "User View",
            rule: "admin",
            authRequired: true
          }
        },
        {
          path: "/admin/user/user-edit/:usersId",
          name: "admin-user-edit",
          component: () => import("@/views/admin/user/user-edit/UserEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('User') },
              { title: i18n.t('Edit'), active: true }
            ],
            pageTitle: i18n.t('edituser'),
            rule: "admin",
            authRequired: true
          }
        },
        {
          path: "/admin/organization",
          name: "admin-company",
          component: () => import("@/views/admin/company/Company.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title:i18n.t('Company')},
              { title: i18n.t('List'), active: true }
            ],
            pageTitle: i18n.t('companylist'),
            rule: "admin",
            authRequired: true
          }
        },
        {
          path: "/admin/request-access",
          name: "request-access",
          component: () => import("@/views/admin/company/RequestAccess.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('RequestAccess')},
              { title: i18n.t('Access'), active: true }
            ],
            pageTitle: i18n.t('RequestAccess'),
            rule: "admin",
            authRequired: true
          }
        },
        {
          path: "/admin/company/company-view/:userId",
          name: "admin-company-view",
          component: () => import("@/views/admin/company/CompanyView.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('User') },
              { title: "View", active: true }
            ],
            pageTitle: "Company View",
            rule: "admin",
            authRequired: true
          }
        },
        {
          path: "/admin/company/company-edit/:userId",
          name: "admin-company-edit",
          component: () => import("@/views/admin/company/company-edit/CompanyEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('User') },
              { title: i18n.t('Edit'), active: true }
            ],
            pageTitle: "Company Edit",
            rule: "admin",
            authRequired: true
          }
        },
        {
          path: "/admin/assign-coach/assign-coach-edit/:userId",
          name: "assign-coach-edit",
          component: () => import("@/views/admin/assign-coach/assign-coach-edit/AssignCoachEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Assign", url: "/" },
              { title: "coach" },
              { title: i18n.t('Edit'), active: true }
            ],
            pageTitle: "Assing Coach Edit",
            rule: "admin",
            authRequired: true
          }
        },
        {
          path: "/user/player-details/all",
          name: "trainee-details-view",
          component: () => import("./views/user/my-coach/trainee-list/AllTraineeListView.vue"),
          meta: {
            rule: "editor",
            authRequired: true,
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('Coachee') },
              { title: i18n.t('List'), active: true }
            ],
            pageTitle: i18n.t('CoacheeList'),
          }
        },
        {
          path: "/admin/coach-assignments",
          name: "admin-assign-coach",
          component: () => import("@/views/admin/assign-coach/AssignCoach.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('AssignCoach') },
              { title: i18n.t('List'), active: true }
            ],
            pageTitle: i18n.t('AssignCoach') ,
            rule: "admin",
            authRequired: true
          }
        },
        {
          path: "/admin/assign-coach/assign-coach-edit/:userId",
          name: "admin-assign-coach-edit",
          component: () => import("@/views/admin/assign-coach/assign-coach-edit/AssignCoachEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('User') },
              { title: i18n.t('Edit'), active: true }
            ],
            pageTitle: "Company Edit",
            rule: "admin",
            authRequired: true
          }
        }
      ]
    },
    {
      // =============================================================================
      // USER GRADIENT LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("./layouts/gradient-layout/Main.vue"),
      children: [
        {
          path: "/user/add-skill",
          name: "add-skill",
          component: () => import("./views/user/add-skill/AddSkill.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/home",
          name: "user-home",
          component: () => import("./views/user/home/Home.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/consultants",
          name: "admin-consultants-list",
          component: () => import("@/views/admin/consultants/ConsultantsList.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: "Consultants", active: true }
            ],
            pageTitle: "Consultants",
            rule: "editor",
            authRequired: true,
          }
        },
        {
          path: "/user/skill-details/:traineeCompetenciesId",
          name: "skill-details",
          component: () => import("./views/user/skill-details/SkillDetails.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        }
      ]
    },
    {
      // =============================================================================
      // USER MAIN LAYOUT ROUTES
      // =============================================================================
      path: "",
      component: () => import("./layouts/main-user/Main.vue"),
      children: [
        {
          path: "/user/add-skill",
          name: "add-skill",
          component: () => import("./views/user/add-skill/AddSkill.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/add-skill/:characteristicId",
          name: "add-skill",
          component: () => import("./views/user/add-skill/AddSkillStep2.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/add-skill/:characteristicId/:competencyId/",
          name: "add-skill",
          component: () => import("./views/user/add-skill/AddSkillStep3.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/my-skills",
          name: "my-skills",
          component: () => import("./views/user/my-skills/MySkills.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/my-coach/profile/view",
          name: "my-coach-profile",
          component: () => import("./views/user/my-coach/CoachProfile.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/coaching-experience",
          name: "coaching-experience",
          component: () => import("./views/user/coaching-experience/CoachingExperience.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/competency-progress/:traineeCompetenciesId",
          name: "competency-progress",
          component: () =>
            import("./views/user/skill-details/competency-progress/CompetencyProgress.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/endorsement-invite/:traineeCompetenciesId",
          name: "endorsement-invite",
          component: () =>
            import("./views/user/skill-details/endorsement-invite/EndorsementInvite.vue"),
          meta: {
            rule: "editor",
            authRequired: true
          }
        },
        {
          path: "/user/profile/view",
          name: "user-profile",
          component: () => import("@/views/user/profile/UserView.vue"),
          meta: {
            authRequired: true,
            pageTitle: "",
            rule: "editor"
          }
        },
        {
          path: "/user/profile/edit",
          name: "user-profile-edit",
          component: () => import("@/views/user/profile/user-edit/UserEdit.vue"),
          meta: {
            authRequired: true,
            rule: "editor"
          }
        },
        {
          path: "/user/trainee-details/view",
          name: "trainee-details-view",
          component: () => import("./views/user/my-coach/trainee-list/TraineeListView.vue"),
          meta: {
            rule: "editor",
            authRequired: true,
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('Coachee') },
              { title: i18n.t('List'), active: true }
            ],
            pageTitle: i18n.t('TraineeDetails')
          }
        },
        {
          path: "/user/my-rater/rater-list",
          name: "my-rater",
          component: () => import("./views/user/my-rater/MyRater.vue"),
          meta: {
            rule: "editor",
            authRequired: true,
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('Rater') },
              { title: i18n.t('List'), active: true }
            ],
            pageTitle: i18n.t('raterDetails')
          }
        },
        {
          path: "/user/training-center/view",
          name: "trainee-details-view",
          component: () => import("./views/user/training-center/trainingCenter.vue"),
          meta: {
            rule: "editor",
            authRequired: true,
          }
        },
        {
          path: "/user/trainee-list/view",
          name: "trainee-list-view",
          component: () => import("./views/user/trainee-list/User.vue"),
          meta: {
            rule: "editor",
            authRequired: true,
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('Coachee') },
              { title: i18n.t('List'), active: true }
            ],
            pageTitle: i18n.t('CoacheeList')
          }
        },
        {
          path: "/user/my-rater/rater-list::usersId",
          name: "my-rater",
          component: () => import("./views/user/trainee-list/MyRater.vue"),
          meta: {
            rule: "editor",
            authRequired: true,
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('Rater') },
              { title: i18n.t('List'), active: true }
            ],
            pageTitle:  i18n.t('raterDetails')
          }
        },
        {
          path: "/user/trainee-list/user-edit/:usersId",
          name: "admin-user-edit",
          component: () => import("@/views/admin/user/user-edit/UserEdit.vue"),
          meta: {
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('User') },
              { title: i18n.t('Edit'), active: true }
            ],
            pageTitle: i18n.t('edituser'),
            rule: "admin",
            authRequired: true
          }
        },
        {
          path: "/user/coaching-experience/view",
          name: "coaching-experience-view",
          component: () => import("./views/user/my-coach/coaching-experience/CoachingExperience.vue"),
          meta: {
            rule: "editor",
            authRequired: true,
            breadcrumb: [
              { title: "Home", url: "/" },
              { title: i18n.t('Trainee') },
              { title: i18n.t('List'), active: true }
            ],
            pageTitle: "Coaching Experience"
          }
        },
        {
          path: '/user/feedback',
          name: "feedback",
          component: () => import("./views/user/feedback.vue") ,
          meta: {
            rule: "editor",
            authRequired:true,
          }
        },
        {
          path: '/user/feedback/*',
          name: "feedback",
          component: () => import("./views/user/feedback.vue")
        },
        {
          path: '/user/changelog',
          name: "feedback",
          component: () => import("./views/changelog.vue") ,
          meta: {
            rule: "editor",
            authRequired:true,
          }
        }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS (No dashboard UI loaded here)
    // =============================================================================
    {
      path: "",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          // External Feedback Response Page (Moved here)
          path: "/user/endorsement-invite/response/:mailType/:externalToken",
          name: "endorsement-invite-response",
          component: () =>
            import("./views/user/skill-details/endorsement-invite/response/Response.vue"),
          meta: {
            rule: "editor",
            authRequired: false
          }
        },
        {
          // Admin Login
          path: "/admin/auth/login",
          name: "admin-login",
          component: () => import("@/views/admin/auth/login/Login.vue"),
          meta: {
            rule: "editor"
          }
        },
        {
          // User Login
          path: "/user/auth/login",
          name: "user-login",
          component: () => import("@/views/user/auth/login/Login.vue"),
          meta: {
            rule: "editor"
          }
        },
        {
          path: '/consultant/login',
          name: 'consultant-login',
          component: () => import('@/views/consultant/auth/ConsultantLogin.vue'),
          meta: {
              rule: 'editor' // Adjust based on your ACL/Auth rules
          }
        },
        {
          path: '/consultant/profile',
          name: 'consultant-profile',
          component: () => import('@/views/consultant/ConsultantProfileView.vue'),
          meta: {
              pageTitle: 'My Profile',
              rule: 'editor' // Adjust according to your ACL
          }
        },
        {
          // User Password Reset
          path: "/user/auth/resetpassowrd/:usersId/:userEmail/:resetToken",
          name: "user-forget-password",
          component: () => import("@/views/user/auth/ResetPassword.vue"),
          meta: {
            rule: "editor"
          }
        },
        {
          // User Logout
          path: "/user/auth/logout",
          name: "user-logout",
          component: () => import("@/views/user/auth/Logout.vue"),
          meta: {
            rule: "editor"
          }
        },
        {
          // User Registration/SignUp
          path: "/user/auth/register/:usersId/:userToken/:company/:userType/:lang",
          name: "user-register",
          component: () => import("@/views/user/auth/register/Register.vue"),
          meta: {
            rule: "editor"
          }
        },
        {
          path: "/user/auth/error",
          name: "error",
          component: () => import("@/views/user/auth/Error.vue"),
          meta: {
            rule: "editor"
          }
        },
        {
          path: "/user/auth/feedback-link-expire",
          name: "FeedbackLinkExpire",
          component: () => import("@/views/user/auth/FeedbackLinkExpire.vue"),
          meta: {
            rule: "editor"
          }
        },
        {
          path: "/user/auth/resetPassword/",
          name: "reset-password",
          component: () => import("@/views/user/auth/ResetPassword.vue"),
          meta: {
            rule: "editor"
          }
        },
        {
          path: "/user/not-authorized",
          name: "user-not-authorized",
          component: () => import("@/views/user/auth/NotAuthorized.vue"),
          meta: {
            rule: "editor"
          }
        },
        {
          path: "/oauth-callback",
          name: "oauth-callback",
          component: () => import("@/views/user/auth/OauthCallback.vue"),
          meta: {
            rule: "editor"
          }
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: "*",
      redirect: "/views/user/auth/Error404",
    }
  ]
});

router.afterEach(() => {
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

router.beforeEach((to, from, next) => {
  if (to.meta.authRequired && to.name !== "endorsement-invite-response") {
    if (to.name !== "user-login" && !auth.isAuthenticated()) {
      next({ name: "user-login" });
    } else {
      next();
    }
  } else if (to.name === "endorsement-invite-response") {
    next();
  } else {
    next();
  }
});

export default router;

// import Vue from "vue";
// import Router from "vue-router";
// import auth from "@/auth/authService";
// import i18n from "./i18n/i18n";

// Vue.use(Router);

// const router = new Router({
//   mode: "history",
//   base: process.env.BASE_URL,
//   scrollBehavior() {
//     return { x: 0, y: 0 };
//   },
//   routes: [
//     {
//       // =============================================================================
//       // MAIN LAYOUT ROUTES
//       // =============================================================================
//       path: "",
//       component: () => import("./layouts/main/Main.vue"),
//       children: [
//         // =============================================================================
//         // Theme Routes
//         // // =============================================================================
//         {
//           path: "/",
//           redirect: "/user/home",
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user",
//           redirect: "/user/home",
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/admin",
//           redirect: "/admin/user",
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/admin/home",
//           redirect: "/admin/user",
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         /*{
//           path: "/admin",
//           name: "Admin Home",
//           component: () => import("./views/admin/Home.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: false
//           }
//         },*/

//         // =============================================================================
//         // Application Routes
//         // =============================================================================


//         {
//           path: "/admin/users",
//           name: "admin-user-list",
//           component: () => import("@/views/admin/user/User.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/"},
//               { title: i18n.t('User') },
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle: i18n.t('userlist'),
//             rule: "admin",
//             authRequired: true,
//           }
//         },
//         {
//           path: "/admin/schedules",
//           name: "calender",
//           component: () => import("@/views/admin/user/calender/Calender.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/"},
//               { title: i18n.t('Schedules') },
//             ],
//             pageTitle: i18n.t('Schedules'),
//             rule: "admin",
//             authRequired: true,
//           }
//         },

//         {
//           path: "/admin/user/user-view/:usersId",
//           name: "admin-user-view",
//           component: () => import("@/views/admin/user/UserView.vue"),
//           meta: {
//             breadcrumb: [
//               { title: i18n.t('User') },
//               { title: "View", active: true }
//             ],
//             pageTitle: "User View",
//             rule: "admin",
//             authRequired: true
//           }
//         },
//         {
//           path: "/admin/user/user-edit/:usersId",
//           name: "admin-user-edit",
//           component: () => import("@/views/admin/user/user-edit/UserEdit.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('User') },
//               { title: i18n.t('Edit'), active: true }
//             ],
//             pageTitle: i18n.t('edituser'),
//             rule: "admin",
//             authRequired: true
//           }
//         },
//         {
//           path: "/admin/organization",
//           name: "admin-company",
//           component: () => import("@/views/admin/company/Company.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title:i18n.t('Company')},
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle: i18n.t('companylist'),
//             rule: "admin",
//             authRequired: true
//           }
//         },
//         {
//           path: "/admin/request-access",
//           name: "request-access",
//           component: () => import("@/views/admin/company/RequestAccess.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('RequestAccess')},
//               { title: i18n.t('Access'), active: true }
//             ],
//             pageTitle: i18n.t('RequestAccess'),
//             rule: "admin",
//             authRequired: true
//           }
//         },
//         {
//           path: "/admin/company/company-view/:userId",
//           name: "admin-company-view",
//           component: () => import("@/views/admin/company/CompanyView.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('User') },
//               { title: "View", active: true }
//             ],
//             pageTitle: "Company View",
//             rule: "admin",
//             authRequired: true
//           }
//         },

//         {
//           path: "/admin/company/company-edit/:userId",
//           name: "admin-company-edit",
//           component: () => import("@/views/admin/company/company-edit/CompanyEdit.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('User') },
//               { title: i18n.t('Edit'), active: true }
//             ],
//             pageTitle: "Company Edit",
//             rule: "admin",
//             authRequired: true
//           }
//         },

//         {
//           path: "/admin/assign-coach/assign-coach-edit/:userId",
//           name: "assign-coach-edit",
//           component: () => import("@/views/admin/assign-coach/assign-coach-edit/AssignCoachEdit.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Assign", url: "/" },
//               { title: "coach" },
//               { title: i18n.t('Edit'), active: true }
//             ],
//             pageTitle: "Assing Coach Edit",
//             rule: "admin",
//             authRequired: true
//           }
//         },
//         /*{
//           path: "/admin/coaching-effectiveness/",
//           name: "admin-coaching-effectiveness",
//           component: () => import("@/views/admin/coaching-effectiveness/CoachingEffectiveness.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: "Coaching Effectiveness" },
//               { title: "View", active: true }
//             ],
//             pageTitle: "Coaching Effectiveness",
//             rule: "editor"
//           }
//         },*/
//         {
//           path: "/user/player-details/all",
//           name: "trainee-details-view",
//           component: () => import("./views/user/my-coach/trainee-list/AllTraineeListView.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true,
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('Coachee') },
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle: i18n.t('CoacheeList'),
//           }
//         },
//         {
//           path: "/admin/coach-assignments",
//           name: "admin-assign-coach",
//           component: () => import("@/views/admin/assign-coach/AssignCoach.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('AssignCoach') },
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle: i18n.t('AssignCoach') ,
//             rule: "admin",
//             authRequired: true
//           }
//         },
//         // {
//         //   path: "/admin/assign-coach/assign-coach-view/:userId",
//         //   name: "admin-assign-coach-view",
//         //   component: () => import("@/views/admin/assign-coach/AssignCoachView.vue"),
//         //   meta: {
//         //     breadcrumb: [
//         //       { title: "Home", url: "/" },
//         //       { title: i18n.t('User') },
//         //       { title: "View", active: true }
//         //     ],
//         //     pageTitle: "Assign-Coach View",
//         //     rule: "admin",
//         //     authRequired: true
//         //   }
//         // },

//         {
//           path: "/admin/assign-coach/assign-coach-edit/:userId",
//           name: "admin-assign-coach-edit",
//           component: () => import("@/views/admin/assign-coach/assign-coach-edit/AssignCoachEdit.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('User') },
//               { title: i18n.t('Edit'), active: true }
//             ],
//             pageTitle: "Company Edit",
//             rule: "admin",
//             authRequired: true
//           }
//         }
//       ]
//     },
//     {
//       // =============================================================================
//       // USER MAIN LAYOUT ROUTES
//       // =============================================================================
//       path: "",
//       component: () => import("./layouts/gradient-layout/Main.vue"),
//       children: [
//         {
//           path: "/user/add-skill",
//           name: "add-skill",
//           component: () => import("./views/user/add-skill/AddSkill.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/home",
//           name: "user-home",
//           component: () => import("./views/user/home/Home.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/skill-details/:traineeCompetenciesId",
//           name: "skill-details",
//           component: () =>
//             import("./views/user/skill-details/SkillDetails.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         /*{
//           path: "/user/add-skill",
//           name: "add-skill",
//           component: () => import("./views/user/add-skill/AddSkill.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/add-skill/:characteristicId",
//           name: "add-skill",
//           component: () => import("./views/user/add-skill/AddSkillStep2.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/add-skill/:characteristicId/:competencyId/",
//           name: "add-skill",
//           component: () => import("./views/user/add-skill/AddSkillStep3.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         }*/
//       ]
//     },
//     {
//       // =============================================================================
//       // USER MAIN LAYOUT ROUTES
//       // =============================================================================
//       path: "",
//       component: () => import("./layouts/main-user/Main.vue"),
//       children: [
//         // {
//         //   path: "/",
//         //   redirect: "/user/add-skill",
//         //   meta: {
//         //     rule: "editor",
//         //     authRequired: true
//         //   }
//         // },
//         // {
//         //   path: "/user",
//         //   redirect: "/user/home",
//         //   meta: {
//         //     rule: "editor",
//         //     authRequired: true
//         //   }
//         // },
//         /*{
//           path: "/user/home",
//           name: "user-home",
//           component: () => import("./views/user/home/Home.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },*/
//         /*{
//           path: "/coach/home",
//           name: "coach-home",
//           component: () => import("./views/coach/profile/UserView.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },*/
//         /*{
//           path: "/user/profile/view",
//           name: "user-profile",
//           component: () => import("./views/user/profile/UserView.vue"),
//           meta: {
//             authRequired: true,
//             rule: "editor"
//           }
//         },*/
//         {
//           path: "/user/add-skill",
//           name: "add-skill",
//           component: () => import("./views/user/add-skill/AddSkill.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/add-skill/:characteristicId",
//           name: "add-skill",
//           component: () => import("./views/user/add-skill/AddSkillStep2.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/add-skill/:characteristicId/:competencyId/",
//           name: "add-skill",
//           component: () => import("./views/user/add-skill/AddSkillStep3.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         /*{
//           path: "/user/skill-details/:traineeCompetenciesId",
//           name: "skill-details",
//           component: () => import('./views/user/skill-details/SkillDetails.vue'),
//           meta: {
//             rule: 'editor',
//             authRequired: true
//           }
//         },*/
//         {
//           path: "/user/my-skills",
//           name: "my-skills",
//           component: () => import("./views/user/my-skills/MySkills.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/my-coach/profile/view",
//           name: "my-coach-profile",
//           component: () => import("./views/user/my-coach/CoachProfile.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },

//         {
//           path: "/user/coaching-experience",
//           name: "coaching-experience",
//           component: () => import("./views/user/coaching-experience/CoachingExperience.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/competency-progress/:traineeCompetenciesId",
//           name: "competency-progress",
//           component: () =>
//             import(
//               "./views/user/skill-details/competency-progress/CompetencyProgress.vue"
//             ),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/endorsement-invite/:traineeCompetenciesId",
//           name: "endorsement-invite",
//           component: () =>
//             import(
//               "./views/user/skill-details/endorsement-invite/EndorsementInvite.vue"
//             ),
//           meta: {
//             rule: "editor",
//             authRequired: true
//           }
//         },
//         {
//           path:
//             "/user/endorsement-invite/response/:mailType/:externalToken",
//           name: "endorsement-invite-response",
//           component: () =>
//             import(
//               "./views/user/skill-details/endorsement-invite/response/Response.vue"
//             ),
//           meta: {
//             rule: "editor",
//             authRequired: false
//           }
//         },
//         /*{
//           path: "/user/add-skill/select-competency",
//           name: "select-competency",
//           component: () => import('./views/user/add-skill/SelectCompetency.vue'),
//           meta: {
//             rule: 'editor'
//           }
//         },
//         {
//           path: "/user/add-skill/rate-your-level",
//           name: "rate-your-level",
//           component: () => import('./views/user/add-skill/RateYourLevel.vue'),
//           meta: {
//             rule: 'editor'
//           }
//         },
//         {
//           path: "/user/add-skill/training-duration",
//           name: "training-duration",
//           component: () => import('./views/user/add-skill/TrainingDuration.vue'),
//           meta: {
//             rule: 'editor'
//           }
//         },
//         {
//           path: "/user/add-skill/competency-progress",
//           name: "competency-progress",
//           component: () => import('./views/user/add-skill/ProgressMeasure.vue'),
//           meta: {
//             rule: 'editor'
//           }
//         },
//         {
//           path: "/user/add-skill/review-skill",
//           name: "review-skill",
//           component: () => import('./views/user/add-skill/ReviewSkill.vue'),
//           meta: {
//             rule: 'editor'
//           }
//         },*/
//         {
//           path: "/user/profile/view",
//           name: "user-profile",
//           component: () => import("@/views/user/profile/UserView.vue"),
//           meta: {
//             authRequired: true,
//             pageTitle: "",
//             rule: "editor"
//           }
//         },
//         {
//           path: "/user/profile/edit",
//           name: "user-profile-edit",
//           component: () =>
//             import("@/views/user/profile/user-edit/UserEdit.vue"),
//           meta: {
//             authRequired: true,
//             rule: "editor"
//           }
//         },
//         {
//           path: "/user/trainee-details/view",
//           name: "trainee-details-view",
//           component: () => import("./views/user/my-coach/trainee-list/TraineeListView.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true,
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('Coachee') },
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle: i18n.t('TraineeDetails')
//           }
//         },
//         {
//           path: "/user/my-rater/rater-list",
//           name: "my-rater",
//           component: () => import("./views/user/my-rater/MyRater.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true,
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('Rater') },
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle: i18n.t('raterDetails')
//           }
//         },
//         {
//           path: "/user/trainee-details/view",
//           name: "trainee-details-view",
//           component: () => import("./views/user/my-coach/trainee-list/TraineeListView.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true,
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('Coachee') },
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle: i18n.t('TraineeDetails')
//           }
//         },
//         {
//           path: "/user/training-center/view",
//           name: "trainee-details-view",
//           component: () => import("./views/user/training-center/trainingCenter.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true,

//           }
//         },
//         {
//           path: "/user/trainee-list/view",
//           name: "trainee-list-view",
//           component: () => import("./views/user/trainee-list/User.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true,
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('Coachee') },
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle: i18n.t('CoacheeList')
//           }
//         },
//         {
//           path: "/user/my-rater/rater-list::usersId",
//           name: "my-rater",
//           component: () => import("./views/user/trainee-list/MyRater.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true,
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('Rater') },
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle:  i18n.t('raterDetails')
//           }
//         },
//         {
//           path: "/user/trainee-list/user-edit/:usersId",
//           name: "admin-user-edit",
//           component: () => import("@/views/admin/user/user-edit/UserEdit.vue"),
//           meta: {
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('User') },
//               { title: i18n.t('Edit'), active: true }
//             ],
//             pageTitle: i18n.t('edituser'),
//             rule: "admin",
//             authRequired: true
//           }
//         },
//         {
//           path: "/user/coaching-experience/view",
//           name: "coaching-experience-view",
//           component: () => import("./views/user/my-coach/coaching-experience/CoachingExperience.vue"),
//           meta: {
//             rule: "editor",
//             authRequired: true,
//             breadcrumb: [
//               { title: "Home", url: "/" },
//               { title: i18n.t('Trainee') },
//               { title: i18n.t('List'), active: true }
//             ],
//             pageTitle: "Coaching Experience"
//           }
//         },
//         {
//           path: '/user/feedback',
//           name: "feedback",
//           component: () => import("./views/user/feedback.vue") ,
//           meta: {
//             rule: "editor",
//             authRequired:true,
//           }
//         },
//         {
//           path: '/user/feedback/*',
//           name: "feedback",
//           component: () => import("./views/user/feedback.vue")
//         },
//         {
//           path: '/user/changelog',
//           name: "feedback",
//           component: () => import("./views/changelog.vue") ,
//           meta: {
//             rule: "editor",
//             authRequired:true,
//           }
//         }
//       ]
//     },
//     // =============================================================================
//     // FULL PAGE LAYOUTS
//     // =============================================================================
//     {
//       path: "",
//       component: () => import("@/layouts/full-page/FullPage.vue"),
//       children: [
//         // =============================================================================
//         // PAGES
//         // =============================================================================
//         {
//           // Admin Login
//           path: "/admin/auth/login",
//           name: "admin-login",
//           component: () => import("@/views/admin/auth/login/Login.vue"),
//           meta: {
//             rule: "editor"
//           }
//         },
//         {
//           // User Login
//           path: "/user/auth/login",
//           name: "user-login",
//           component: () => import("@/views/user/auth/login/Login.vue"),
//           meta: {
//             rule: "editor"
//           }
//         },
//         {
//           // User Login
//           path: "/user/auth/resetpassowrd/:usersId/:userEmail/:resetToken",
//           name: "user-forget-password",
//           component: () => import("@/views/user/auth/ResetPassword.vue"),
//           meta: {
//             rule: "editor"
//           }
//         },
//         {
//           // User Logout
//           path: "/user/auth/logout",
//           name: "user-logout",
//           component: () => import("@/views/user/auth/Logout.vue"),
//           meta: {
//             rule: "editor"
//           }
//         },
//         {
//           // User Registration/SignUp
//           path: "/user/auth/register/:usersId/:userToken/:company/:userType/:lang",
//           name: "user-register",
//           component: () => import("@/views/user/auth/register/Register.vue"),
//           meta: {
//             rule: "editor"
//           }
//         },
//         {
//           // User Registration/SignUp
//           path: "/user/auth/error",
//           name: "error",
//           component: () => import("@/views/user/auth/Error.vue"),
//           meta: {
//             rule: "editor"
//           }
//         },
//         {
//           // User Registration/SignUp
//           path: "/user/auth/feedback-link-expire",
//           name: "FeedbackLinkExpire",
//           component: () => import("@/views/user/auth/FeedbackLinkExpire.vue"),
//           meta: {
//             rule: "editor"
//           }
//         },
//         {
//           path: "/user/auth/resetPassword/",
//           name: "reset-password",
//           component: () => import("@/views/user/auth/ResetPassword.vue"),
//           meta: {
//             rule: "editor"
//           }
//         },

//         {
//           path: "/user/not-authorized",
//           name: "user-not-authorized",
//           component: () => import("@/views/user/auth/NotAuthorized.vue"),
//           meta: {
//             rule: "editor"
//           }
//         },

//         {
//           path: "/oauth-callback",
//           name: "oauth-callback",
//           component: () => import("@/views/user/auth/OauthCallback.vue"),
//           meta: {
//             rule: "editor"
//           }
//         }
//       ]
//     },
//     // Redirect to 404 page, if no match found
//     {
//       path: "*",
//       redirect: "/views/user/auth/Error404",
//     }
//   ]
// });

// router.afterEach(() => {
//   // Remove initial loading
//   const appLoading = document.getElementById("loading-bg");
//   if (appLoading) {
//     appLoading.style.display = "none";
//   }
// });

// router.beforeEach((to, from, next) => {
//   if (to.meta.authRequired && to.name !== "endorsement-invite-response") {
//     if (to.name !== "user-login" && !auth.isAuthenticated()) {
//       next({ name: "user-login" });
//     } else {
//       // console.log(to, from)
//       next();
//     }
//   } else if (to.name === "endorsement-invite-response") {
//     next();
//   } else {
//     next();
//   }

//   // next()
// });

// export default router;
